import React, { useEffect, useState, useCallback } from 'react';
import { Send, Check } from 'lucide-react';
import { callAPI } from '../services/callApi';
// Persistent "email the people on this call?" prompt for calls S scheduled.
// Mounted app-wide (next to the S agent) so it reaches the owner anywhere; it
// does NOT auto-dismiss — it waits until the owner chooses Send or "I'll do it".
// The pending state lives on the call record (clientNotifyPending), so it
// survives navigation and reload.
//
// S-AGENT TOAST STANDARD: every toast that comes from the S agent carries the
// S-Agent identity header (SAgentToastHeader) so the owner instantly knows S
// did it. Reuse SAgentToastHeader for any future S-agent toast (task done /
// needs a decision) and keep the same card shell.
export function SAgentToastHeader({ tag }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <span style={{ width: 24, height: 24, borderRadius: 7, background: 'linear-gradient(135deg,#1a2133,#0d1322)', border: '1px solid rgba(212,175,55,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#F1D98A', fontWeight: 800, fontSize: 13, flex: 'none' }}>S</span>
      <span style={{ fontSize: 12.5, fontWeight: 700, color: '#F1D98A', letterSpacing: '0.01em' }}>S Agent</span>
      {tag ? <span style={{ marginLeft: 'auto', fontSize: 9.5, color: '#6f7889', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 999, padding: '2px 7px', whiteSpace: 'nowrap' }}>{tag}</span> : null}
    </div>
  );
}
export default function PendingCallNotifier() {
  const [items, setItems] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await callAPI.pendingNotify();
      setItems(Array.isArray(r && r.pending) ? r.pending : []);
    } catch (e) { /* quiet — retry next tick */ }
  }, []);
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [load]);
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
    } catch (e) { return ''; }
  };
  const send = async (it) => {
    setBusyId(it.id);
    try { await callAPI.sendInvites(it.id); setItems((p) => p.filter((x) => x.id !== it.id)); }
    catch (e) { /* leave it up so they can retry */ }
    finally { setBusyId(null); }
  };
  const dismiss = async (it) => {
    setBusyId(it.id);
    try { await callAPI.dismissNotify(it.id); setItems((p) => p.filter((x) => x.id !== it.id)); }
    catch (e) { /* leave it up */ }
    finally { setBusyId(null); }
  };
  if (!items.length) return null;
  return (
    <div style={{ position: 'fixed', right: 22, bottom: 22, width: 360, maxWidth: 'calc(100vw - 32px)', zIndex: 10050, display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none' }}>
      {items.slice(0, 3).map((it) => (
        <div key={it.id} style={{ pointerEvents: 'auto', background: 'linear-gradient(160deg, rgba(20,26,42,0.98), rgba(12,17,32,0.99))', border: '1px solid rgba(212,175,55,0.35)', borderRadius: 14, padding: '15px 16px', boxShadow: '0 18px 50px rgba(0,0,0,0.55)', fontFamily: 'Figtree, system-ui, sans-serif' }} data-testid={`pending-notify-${it.id}`}>
          <SAgentToastHeader tag="stays until you choose" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 11 }}>
            <span style={{ width: 20, height: 20, borderRadius: '50%', background: 'rgba(74,222,128,0.14)', border: '1px solid rgba(74,222,128,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#4ade80', flex: 'none' }}><Check className="w-3 h-3" /></span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Call scheduled</span>
          </div>
          <div style={{ fontSize: 12.5, color: '#aeb6c6', lineHeight: 1.5, margin: '9px 0 13px' }}>
            Your call with <b style={{ color: '#fff' }}>{it.contactName}</b>{it.startAt ? (<> — <b style={{ color: '#fff' }}>{fmt(it.startAt)}</b></>) : null} is on your Call Schedule. Want me to email everyone on it the details?
          </div>
          <div style={{ display: 'flex', gap: 9 }}>
            <button onClick={() => send(it)} disabled={busyId === it.id} style={{ flex: 1, fontSize: 12, fontWeight: 700, padding: 9, borderRadius: 10, border: 'none', cursor: 'pointer', background: 'linear-gradient(135deg,#F1D98A,#D4AF37)', color: '#0B0F1A', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, opacity: busyId === it.id ? 0.6 : 1 }} data-testid={`pending-notify-send-${it.id}`}>
              <Send className="w-3.5 h-3.5" /> {busyId === it.id ? 'Sending…' : 'Send the email'}
            </button>
            <button onClick={() => dismiss(it)} disabled={busyId === it.id} style={{ fontSize: 12, fontWeight: 600, padding: '9px 14px', borderRadius: 10, cursor: 'pointer', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.12)', color: '#8A93A6' }} data-testid={`pending-notify-dismiss-${it.id}`}>I'll do it</button>
          </div>
        </div>
      ))}
    </div>
  );
}
